import { PaletteOutlined, TuneOutlined } from '@mui/icons-material';
import { Alert, Box, FormControl, FormLabel, InputAdornment, Stack, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import {
  ChoiceControl, ChoiceGroup, FormTextField, SectionCard, brandThemePresets,
  createFocusRingStyles, designTokens, resolveBrandTheme,
  type BrandSurfaceTone, type BrandThemePresetID,
} from '../../../design-system';
import { validHexColor } from '../model/organization';
import type { OrganizationSettings } from '../model/types';
import { AppearancePreview } from './AppearancePreview';
import { AppearanceThemeThumbnail } from './AppearanceThemeThumbnail';

const colorFields = [
  ['primaryColor', '주요 강조색', '버튼과 선택 표시'],
  ['accentColor', '보조 강조색', '프로필과 분류 배지'],
  ['navigationColor', '사이드바 색상', '메뉴 영역의 바탕'],
] as const;
const surfaceOptions = [
  { value: 'cool', label: '쿨 그레이' },
  { value: 'neutral', label: '뉴트럴 그레이' },
  { value: 'warm', label: '웜 그레이' },
] as const;

type AppearanceEditorProps = {
  draft: OrganizationSettings;
  baseline: OrganizationSettings;
  resolvedTheme: ReturnType<typeof resolveBrandTheme>;
  action?: ReactNode;
  error?: string;
  canWrite: boolean;
  saving: boolean;
  onSelectPreset: (preset: BrandThemePresetID) => void;
  onChangeColor: (field: 'primaryColor' | 'navigationColor' | 'accentColor', value: string) => void;
  onChangeSurfaceTone: (tone: BrandSurfaceTone) => void;
};

export function AppearanceEditor({ draft, baseline, resolvedTheme, action, error, canWrite, saving, onSelectPreset, onChangeColor, onChangeSurfaceTone }: AppearanceEditorProps) {
  const theme = draft.brandTheme;
  const unavailable = !canWrite || saving;
  return (
    <Box component="section" aria-label="색상 세트">
      <SectionCard help="색상 변경은 저장 후 전체 화면에 적용됩니다. 사용자 지정 색상에는 읽기 편한 명암을 적용하며 완료·주의·오류 상태 색은 유지됩니다." bodyPadding="none" headerPadding="standard" stickyHeader showDivider={false} action={action}
        title={<Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}><PaletteOutlined color="action" fontSize="small" /><span>색상 세트</span></Stack>}>
        <Box sx={{ px: { xs: 2, sm: 3 }, pb: 3, pt: 1 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(300px, 0.72fr) minmax(0, 1.28fr)' }, gap: { xs: 3, md: 4, lg: 5 }, alignItems: 'start' }}>
          <Stack spacing={3}>
            <FormControl component="fieldset" disabled={unavailable}>
              <FormLabel id="brand-theme-preset-label" component="legend" sx={{ color: 'text.primary', ...designTokens.typography.subsectionTitle }}>우리 회사에 어울리는 테마</FormLabel>
              <ChoiceGroup aria-labelledby="brand-theme-preset-label" name="brand-theme-preset" value={theme.presetId} onValueChange={(value) => onSelectPreset(value as BrandThemePresetID)}>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1.25 }}>
                  {brandThemePresets.map((preset) => {
                    const selected = theme.presetId === preset.id;
                    const custom = preset.id === 'custom-v1';
                    const palette = custom ? resolvedTheme : resolveBrandTheme({ presetId: preset.id, surfaceTone: theme.surfaceTone });
                    return (
                      <ChoiceControl key={preset.id} data-testid={custom ? 'brand-theme-custom-card' : 'brand-theme-preset-card'}
                        value={preset.id} kind="radio" size="small" disabled={unavailable}
                        controlSx={{ position: 'absolute', bottom: 6, right: 2 }}
                        label={<Box sx={{ p: 1, pb: 0 }}>
                          {custom ? (
                            <Stack aria-hidden sx={{ height: 57, alignItems: 'center', justifyContent: 'center', gap: 0.5, border: '1px dashed', borderColor: designTokens.color.strongDivider, borderRadius: designTokens.shape.md, bgcolor: designTokens.color.neutralRaisedSurface }}>
                              <TuneOutlined sx={{ color: designTokens.color.secondaryText, fontSize: 22 }} />
                              <Typography variant="caption" color="text.secondary">내 색상으로 조정</Typography>
                            </Stack>
                          ) : <AppearanceThemeThumbnail palette={palette} />}
                          <Typography variant="body2" sx={{ minHeight: 38, display: 'flex', alignItems: 'center', pr: 3.5, fontWeight: selected ? 600 : 500 }}>{preset.label}</Typography>
                        </Box>}
                        sx={{ m: 0, position: 'relative', minWidth: 0, alignItems: 'stretch', border: '1px solid', borderColor: selected ? designTokens.color.action : designTokens.color.divider, borderRadius: designTokens.radius.navigation, bgcolor: selected ? designTokens.color.actionSoft : designTokens.color.canvas,
                          transition: 'border-color 120ms ease, background-color 120ms ease',
                          '&:hover': { borderColor: unavailable ? undefined : designTokens.color.brandBorder },
                          '&:focus-within': createFocusRingStyles({ inset: true }),
                          '& .MuiFormControlLabel-label': { width: '100%', minWidth: 0 },
                          '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
                        }} />
                    );
                  })}
                </Box>
              </ChoiceGroup>
            </FormControl>
            {theme.presetId === 'custom-v1' && (
              <Stack data-testid="brand-theme-custom-fields" spacing={1.5}>
                <Typography component="h3" sx={designTokens.typography.subsectionTitle}>색상 조정</Typography>
                {colorFields.map(([field, label, helper]) => {
                  const value = theme[field] ?? resolvedTheme[field];
                  const valid = validHexColor(value);
                  return (
                    <FormTextField key={field} fullWidth label={`${label} HEX`} value={value} disabled={unavailable} error={!valid}
                      help={helper} helperText={valid ? undefined : '#을 포함한 6자리 HEX를 입력해 주세요.'} onChange={(event) => onChangeColor(field, event.target.value)}
                      slotProps={{ input: { startAdornment: <InputAdornment position="start" sx={{ m: 0, maxHeight: 'none' }}>
                        <Box component="input" id={`brand-${field}-color-picker`} type="color" aria-label={`${label.replace(/ 색상$/, '')} 색상 선택`} value={valid ? value : resolvedTheme[field]} disabled={unavailable} onChange={(event) => onChangeColor(field, event.target.value)}
                          sx={{ width: 40, height: 40, boxSizing: 'border-box', p: '8px', border: 0, borderRadius: designTokens.shape.sm, bgcolor: 'transparent', cursor: unavailable ? 'default' : 'pointer', [designTokens.media.touchTarget]: { width: 44, height: 44, p: '10px' }, '&::-webkit-color-swatch-wrapper': { p: 0 }, '&::-webkit-color-swatch': { border: `1px solid ${designTokens.color.strongDivider}`, borderRadius: designTokens.shape.sm }, '&::-moz-color-swatch': { border: `1px solid ${designTokens.color.strongDivider}`, borderRadius: designTokens.shape.sm }, '&:focus-visible': createFocusRingStyles({ inset: true }), '@media (forced-colors: active)': { '&::-webkit-color-swatch': { forcedColorAdjust: 'none', outline: '1px solid CanvasText' }, '&::-moz-color-swatch': { forcedColorAdjust: 'none', outline: '1px solid CanvasText' } } }} />
                      </InputAdornment> }, htmlInput: { maxLength: 7, spellCheck: false } }} />
                  );
                })}
              </Stack>
            )}
            <FormControl component="fieldset" disabled={unavailable}>
              <FormLabel id="brand-surface-tone-label" component="legend" sx={{ mb: 1.25, color: 'text.primary', ...designTokens.typography.subsectionTitle }}>배경 분위기</FormLabel>
              <ChoiceGroup aria-labelledby="brand-surface-tone-label" name="brand-surface-tone" value={theme.surfaceTone ?? 'cool'} onValueChange={(value) => onChangeSurfaceTone(value as BrandSurfaceTone)}>
                <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap' }} useFlexGap>
                  {surfaceOptions.map(({ value, label }) => (
                    <ChoiceControl key={value} kind="radio" size="small" value={value} disabled={unavailable} label={label} edgeAlignment="control"
                      sx={{ m: 0, pr: 1, py: 0.25, bgcolor: resolveBrandTheme({ ...theme, surfaceTone: value }).pageCanvasColor, border: '1px solid', borderColor: (theme.surfaceTone ?? 'cool') === value ? designTokens.color.brandBorder : designTokens.color.divider, borderRadius: designTokens.radius.pill, '&:focus-within': createFocusRingStyles({ inset: true }), '& .MuiFormControlLabel-label': { fontSize: '0.75rem' } }} />
                  ))}
                </Stack>
              </ChoiceGroup>
            </FormControl>
          </Stack>
          <Box sx={{ position: { md: 'sticky' }, top: { md: 96 }, minWidth: 0 }}>
            <AppearancePreview draft={draft} baseline={baseline} resolvedTheme={resolvedTheme} />
          </Box>
        </Box>
        </Box>
      </SectionCard>
    </Box>
  );
}
